import React, { useEffect } from "react";
import { connect } from "react-redux";
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Divider from "@mui/material/Divider";
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { cardStyle } from "../../../Dashboard/customStyles";
import { generateAssumptions, updateAssumptions } from "../../../../store/modules/waterfall/actions";
import { updateLBODashboard } from "../../../../store/modules/dealOffer/actions";
import { primary } from "../../../../assets/colorPallet";
import BVAInput from "../../../../components/BVAInput/BVAInput";
import Item from "../InputItem";
import YearBar from "../YearBar";
import Subtitle from "./Subtitle";

function GeneralAssumptions({
  dealType,
  assumptions,
  dispatchGenerateAssumptions,
  dispatchUpdateAssumptions,
  dispatchUpdateLBODashboard
}) {
  // Mounted
  useEffect(() => {
    dispatchGenerateAssumptions(dealType);
    //dispatchGenerateLBODashboard(dealType);     //included
  }, []);

  const handleChange = (name, value, type = "Common", index = 0) => {
    if(!(assumptions.independence === "MANUAL" && name === "exitProceedsType" && value === "AUTO"))
      dispatchUpdateAssumptions(name, value, type, index);  //when model is independent, exitProceedsType can't be automatic
    if(name === "reinvestRate") dispatchUpdateLBODashboard("reinvestmentRate", value);   //link to deal offer settings
  };

  return (
    <>
      {assumptions &&
        (<>
          <div className="flex flex-wrap justify-center">
            <Card sx={cardStyle.dealCard}>
              <CardHeader
                sx={{ background: primary }}
                titleTypographyProps={{ fontSize: 40, fontWeight: 700, color: "white", textAlign: "center" }}
                title="General Input"
              />
              <CardContent sx={{ padding: "20px 30px" }}>
                <div className="flex text-center justify-between items-center my-3 px-5">
                  <div className="items-center text-lg">Basis for Return Calculations</div>
                  <FormControl variant="standard" sx={{ m: 1, width: 155 }}>
                    <Select
                      value={assumptions.basis}
                      onChange={(e) => handleChange("basis", e.target.value)}
                    >
                      <MenuItem value={"IRR"}>IRR</MenuItem>
                      <MenuItem value={"COC"}>Cash on Cash</MenuItem>
                      <MenuItem value={"MULTI"}>IRR + EM Combined</MenuItem>
                    </Select>
                  </FormControl>
                </div>
                <div className="flex text-center justify-between items-center my-3 px-5">
                  <div className="items-center text-lg">Proceeds Distributed</div>
                  <FormControl variant="standard" sx={{ m: 1, width: 155 }}>
                    <Select
                      value={assumptions.proceeds}
                      onChange={(e) => handleChange("proceeds", e.target.value)}
                    >
                      <MenuItem value={"PR"}>Pro Rata</MenuItem>
                      <MenuItem value={"PPS"}>Per Preference Schedule</MenuItem>
                    </Select>
                  </FormControl>
                </div>
                <Item
                  name="WACC Used for Valuation"
                  data={assumptions.financeRate}
                  endLabel="%"
                  handleChange={(value) => handleChange("financeRate", value)}
                />
                <Item
                  name="Reinvestment Rate for MIRR"
                  data={assumptions.reinvestRate}
                  endLabel="%"
                  handleChange={(value) => handleChange("reinvestRate", value)}
                />
              </CardContent>
            </Card>
          </div>

          <div className="flex flex-wrap justify-center mb-20">
            <Card sx={cardStyle.dealCard}>
              <CardHeader
                sx={{ background: primary }}
                titleTypographyProps={{ fontSize: 40, fontWeight: 700, color: "white",  textAlign: "center" }}
                title="Purchase Assumptions"
              />
              <CardContent sx={{ padding: "20px 30px" }}>
                <Item
                  name="Purchase Price"
                  data={assumptions.independence === "AUTO" ? assumptions.purchasePrice : assumptions.manualPurchasePrice}
                  disabled={assumptions.independence === "AUTO"}
                  handleChange={(value) => assumptions.independence === "MANUAL" && handleChange("manualPurchasePrice", value)}
                />
                <Item
                  name="Transaction Costs"
                  data={assumptions.transactionCosts}
                  disabled={assumptions.independence === "AUTO"}
                  handleChange={(value) => assumptions.independence === "MANUAL" && handleChange("manualTransactionCosts", value)}
                />
                <Item
                  name="&nbsp;&nbsp;&nbsp;Closing Fees (of Transaction Costs)"
                  data={assumptions.closingFees}
                  disabled={assumptions.independence === "AUTO"}
                  handleChange={(value) => assumptions.independence === "MANUAL" && handleChange("manualClosingFees", value)}
                />
                <Item
                  name="Target Debt Amount"
                  data={assumptions.independence === "AUTO" ? assumptions.targetDebt : assumptions.manualTargetDebt}
                  disabled={assumptions.independence === "AUTO"}
                  handleChange={(value) => assumptions.independence === "MANUAL" && handleChange("manualTargetDebt", value)}
                />
                <Item
                  name="Debt Fees"
                  data={assumptions.debtFees}
                  disabled={assumptions.independence === "AUTO"}
                  handleChange={(value) => assumptions.independence === "MANUAL" && handleChange("manualDebtFees", value)}
                />
                <Item
                  name="(-) Cash on Target Balance Sheet"
                  data={assumptions.cash}
                  disabled={assumptions.independence === "AUTO"}
                  handleChange={(value) => assumptions.independence === "MANUAL" && handleChange("manualCash", value)}
                />
                <Item
                  name="Minimum Cash Balance"
                  data={assumptions.minimumCashBalance}
                  disabled={assumptions.independence === "AUTO"}
                  handleChange={(value) => assumptions.independence === "MANUAL" && handleChange("manualMinimumCashBalance", value)}
                />
                <Item
                  name="(-) Additional Debt (Net Debt) Amount"
                  data={assumptions.additionalDebt}
                  disabled={assumptions.independence === "AUTO"}
                  handleChange={(value) => assumptions.independence === "MANUAL" && handleChange("manualAdditionalDebt", value)}
                />
                <Item
                  name="(-) Seller Rollover Equity"
                  data={assumptions.rolloverEquity}
                  disabled={assumptions.independence === "AUTO"}
                  handleChange={(value) => assumptions.independence === "MANUAL" && handleChange("manualRolloverEquity", value)}
                />
                <Divider />
                <Item name="Total Equity Required" data={assumptions.totalEquity} disabled={true} bold={true} />
              </CardContent>
            </Card>

            <Card sx={cardStyle.dealCard}>
              <CardHeader
                sx={{ background: primary }}
                titleTypographyProps={{ fontSize: 40, fontWeight: 700, color: "white",  textAlign: "center" }}
                title="Exit Assumptions"
              />
              <CardContent sx={{ padding: "20px 30px" }}>
                <Item
                  name="Exit Year"
                  data={assumptions.exitYear}
                  endLabel="Years"
                  disabled={assumptions.independence === "AUTO"}
                  handleChange={(value) => assumptions.independence === "MANUAL" && handleChange("manualExitYear", value)}
                />
                <div className="flex justify-between items-center my-3 px-5">
                  <div className="flex items-center">
                    <div className="items-center text-lg mr-4">Exit Proceeds</div>
                    <FormControl variant="standard" sx={{ m: 1, minWidth: 150 }}>
                      <Select
                        labelId="demo-simple-select-standard-label"
                        id="demo-simple-select-standard"
                        value={assumptions.exitProceedsType}
                        onChange={(e) => handleChange("exitProceedsType", e.target.value)}
                      >
                        <MenuItem value={"AUTO"}>From FCFF</MenuItem>
                        <MenuItem value={"MANUAL"}>Manual Input</MenuItem>
                      </Select>
                    </FormControl>
                  </div>
                  <BVAInput
                    size="small"
                    width="170px"
                    textAlign="start"
                    disabled={assumptions.exitProceedsType === "AUTO" ? true : false}
                    endAdornment
                    endAdornmentLabel="$"
                    value={assumptions.exitProceeds}
                    onChange={(value) => assumptions.exitProceedsType === "MANUAL" && handleChange("manualExitProceeds", value)}
                  />
                </div>
                <Item
                  name="Exit Fees Percentage"
                  data={assumptions.exitFeesPercent}
                  endLabel="%"
                  handleChange={(value) => handleChange("exitFeesPercent", value)}
                />
                <Item name="Exit Fees Amount" data={assumptions.exitFees} disabled={true} />
                <Item
                  name="Other Exit Fees"
                  data={assumptions.otherExitFees}
                  handleChange={(value) => handleChange("otherExitFees", value)}
                />
                <Item name="Gross Proceeds to Equity Holders" data={assumptions.grossProceeds} disabled={true} />
                <Item
                  name="Outstanding Debt"
                  data={assumptions.outstandingDebt}
                  disabled={assumptions.independence === "AUTO"}
                  handleChange={(value) => assumptions.independence === "MANUAL" && handleChange("manualOutstandingDebt", value)}
                />
                <Divider />
                <Item name="Net Proceeds to Equity Holders" data={assumptions.netProceeds} disabled={true} bold={true} />
              </CardContent>
            </Card>
          </div>

          <Subtitle title="Free Cash Flow to Equity Holders" length={assumptions.yearsFormatted?.length} />
          <YearBar data={assumptions.yearsFormatted} />
          <div className="flex items-center mt-5">
            <div className="flex items-center pt-3" style={{ minWidth: "12.1em" }}>
              Free Cash Flow
            </div>
            {assumptions.freeCashFlow?.map((obj, index, arr) => (
              <div className="flex pt-5" style={{ minWidth: "10em" }}>
                <BVAInput
                  startAdornment
                  startAdornmentLabel="$ "
                  width="150px"
                  size="small"
                  disabled={assumptions.independence === "AUTO"}
                  value={obj.value}
                  onChange={(value) => {
                    let newArray = arr.map(obj => ({...obj}));
                    newArray[index].value = Number(value.replace(/,/g, ""));
                    if(assumptions.independence === "MANUAL") return handleChange("manualFreeCashFlow", newArray);
                  }}
                />
              </div>
            ))}
          </div>
        </>)
      }
    </>
  );
}

const mapStateToProps = (state) => ({
  assumptions: state.waterfall.assumptions,
});

const mapDispatchToProps = (dispatch) => ({
  async dispatchGenerateAssumptions(dealType) {
    await dispatch(generateAssumptions(dealType));
  },
  async dispatchUpdateAssumptions(name, value, type, index) {
    await dispatch(updateAssumptions(name, value, type, index));
  },
  async dispatchUpdateLBODashboard(name, value) {
    await dispatch(updateLBODashboard(name, value));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(GeneralAssumptions);
